'use client';

import Link from 'next/link';
import { useCallback, useEffect } from 'react';
import { useLivePolling } from '@/src/lib/use-live-polling';
import { listNotificationsAction } from '@/src/server/actions/notifications.actions';

interface NotificationRow {
  public_id: string;
  title: string;
  message: string;
  status: string;
}

export function LiveNotificationsPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const fetcher = useCallback(async (): Promise<NotificationRow[]> => {
    const result = await listNotificationsAction();
    if (result.error) throw new Error(result.error);
    return (result.data ?? []).filter((entry) => typeof entry.public_id === 'string' && entry.status !== 'dismissed');
  }, []);
  const { data, error, isLoading } = useLivePolling({ fetcher, stop: !isOpen });

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="Benachrichtigungen" className="fixed inset-0 z-[76] flex justify-end bg-slate-900/55"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="h-full w-full max-w-md overflow-y-auto border-l border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-5 text-[var(--k-color-text-primary)]">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Benachrichtigungen</h2>
          <button type="button" onClick={onClose} aria-label="Benachrichtigungen schließen" className="rounded border border-[var(--k-color-border-default)] px-3 py-2">Schließen</button>
        </div>
        {error ? <p role="alert" className="mt-5 text-sm text-[var(--k-color-danger-strong)]">Benachrichtigungen konnten nicht geladen werden.</p>
          : !data && isLoading ? <p role="status" className="mt-5 text-sm">Wird geladen…</p>
          : data?.length === 0 ? <p className="mt-5 text-sm text-[var(--k-color-text-muted)]">Keine Benachrichtigungen vorhanden.</p>
          : <ul className="mt-5 space-y-3">{data?.map((entry) => (
            <li key={entry.public_id} className="rounded border border-[var(--k-color-border-default)] p-3">
              <p className="font-semibold">{entry.title}</p>
              <p className="mt-1 text-sm text-[var(--k-color-text-secondary)]">{entry.message}</p>
              {entry.status === 'unread' ? <span className="mt-2 inline-block text-xs font-semibold text-[var(--k-color-text-link)]">Ungelesen</span> : null}
            </li>
          ))}</ul>}
        <Link href="/benachrichtigungen" onClick={onClose} className="mt-5 inline-block text-sm font-semibold text-[var(--k-color-text-link)] underline">Alle Benachrichtigungen verwalten</Link>
      </section>
    </div>
  );
}
